import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref, computed } from 'vue'
import type { AccessLevel, RowScope, ModulePermission } from '@/types/architecture'
import { fetchPermissions } from '@/core/localAuth'

/**
 * Weight map for comparing access levels.
 * Higher weight = more permissive.
 */
const ACCESS_LEVEL_WEIGHT: Record<AccessLevel, number> = {
    none: 0,
    view_only: 1,
    standard: 2,
    full: 3,
}

/** Default permission returned when a module key is not present in the response */
const DEFAULT_PERMISSION: ModulePermission = { accessLevel: 'none', rowScope: 'own' }

/**
 * Permission Store — manages resolved module permissions from the backend RBAC system.
 *
 * Fetches permissions from GET /auth/me/permissions and provides
 * canView/canEdit/canAdmin helpers for UI gating.
 */
export const usePermissionStore = defineStore('permissions', () => {
    // ── State ────────────────────────────────────────────────────────────
    const modules = ref<Record<string, ModulePermission>>({})
    const isLoaded = ref(false)
    const isLoading = ref(false)
    const error = ref<string | null>(null)

    // ── Getters ──────────────────────────────────────────────────────────

    /** All loaded module keys */
    const moduleKeys = computed(() => Object.keys(modules.value))

    // ── Actions ──────────────────────────────────────────────────────────

    /**
     * Fetch resolved permissions from GET /auth/me/permissions.
     * Called after login, org switch, and token refresh.
     */
    async function load(): Promise<void> {
        if (isLoading.value) return // Prevent duplicate requests

        isLoading.value = true
        error.value = null

        try {
            const response = await fetchPermissions()
            modules.value = response?.modules ?? {}
            isLoaded.value = true
        } catch (err) {
            console.error('Failed to load permissions:', err)
            error.value = err instanceof Error ? err.message : 'Failed to load permissions'
            // On error, keep existing permissions (stale data > no data)
            // But mark as loaded so the app doesn't block on retry
            isLoaded.value = true
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Get the full permission object for a module key.
     * Returns DEFAULT_PERMISSION if the key is not found.
     */
    function getPermission(moduleKey: string): ModulePermission {
        return modules.value[moduleKey] ?? DEFAULT_PERMISSION
    }

    /**
     * Check if the user has at least the required access level for a module.
     *
     * @param moduleKey - Backend module key (e.g., 'fleet', 'settings.users')
     * @param requiredLevel - Minimum access level required
     * @returns true if user's access level >= required level
     */
    function can(moduleKey: string, requiredLevel: AccessLevel): boolean {
        const permission = getPermission(moduleKey)
        return ACCESS_LEVEL_WEIGHT[permission.accessLevel] >= ACCESS_LEVEL_WEIGHT[requiredLevel]
    }

    /**
     * Check if the user can view a module (access level >= view_only).
     */
    function canView(moduleKey: string): boolean {
        return can(moduleKey, 'view_only')
    }

    /**
     * Check if the user can edit in a module (access level >= standard).
     */
    function canEdit(moduleKey: string): boolean {
        return can(moduleKey, 'standard')
    }

    /**
     * Check if the user can administer a module (access level = full).
     */
    function canAdmin(moduleKey: string): boolean {
        return can(moduleKey, 'full')
    }

    /**
     * Get the row scope for a module.
     */
    function rowScope(moduleKey: string): RowScope {
        return getPermission(moduleKey).rowScope
    }

    /**
     * Check if the user has canView on any of the provided module keys.
     * Useful for container modules like Settings that should show if any child is visible.
     */
    function canViewAny(moduleKeys: string[]): boolean {
        return moduleKeys.some(key => canView(key))
    }

    /**
     * Clear all permission state. Called on logout.
     */
    function clear(): void {
        modules.value = {}
        isLoaded.value = false
        isLoading.value = false
        error.value = null
    }

    return {
        // State
        modules,
        isLoaded,
        isLoading,
        error,
        // Getters
        moduleKeys,
        // Actions
        load,
        getPermission,
        can,
        canView,
        canEdit,
        canAdmin,
        rowScope,
        canViewAny,
        clear,
    }
})

// Preserve state during Vite HMR (dev-only)
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(usePermissionStore, import.meta.hot))
}
